import React from 'react';
import { Link } from 'react-router-dom';
import { CommentItemShape, UserShape, PeopleSearchShape } from '../../shapes';
import DateMaster from '../../stores/utils/date_master';
import CommentPeopleMenu from './comment_people_menu';
import CommentLikeButton from '../comment_like_button';
import ContentEditable from 'react-contenteditable';
import sanitizeHtml from "sanitize-html";
import PropTypes from 'prop-types';

const AT_MATCH_REGEX = /@(\w{2,})(?!(.*<\/a>))/gmu;

export default class Comment extends React.Component {

  static propTypes = {
    writeable: PropTypes.bool,
    user: UserShape,
    peopleSearchObject: PeopleSearchShape,
    comment: CommentItemShape,
    onDeleteComment: PropTypes.func,
    onSaveComment: PropTypes.func,
    onCancelNewComment: PropTypes.func,
    onSearchForPeople: PropTypes.func,
    onLikeComment: PropTypes.func,
    onUnLikeComment: PropTypes.func
  }

  state = {
    editing: this.props.writeable,
    html: this.props.comment.get('content') || "",
    peopleTextSearchString: null
  }

  contentEditableRef = React.createRef();

  componentDidMount() {
    if (this.state.editing) {
      this.contentEditableRef.current.focus();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // There's a bizarre condition where, after saving, the comment object
    // comes back with wordText populated, and nothing else. It then updates
    // again with requisite attributes. No time to investigate now,
    // so I've inserted this workaround.
    if (!nextState.editing && !this.props.comment.get('slug')) {
      return false;
    } else {
      return true;
    }
  }

  render() {
    const contentClasses = (this.state.editing) ? 'content editable' : 'content';
    return (
      <div className='comment'>
        { this.renderPersonMeta() }
        <div className={ contentClasses }>
          <ContentEditable
            innerRef={ this.contentEditableRef }
            className='content-editable'
            tagName="div"
            disabled={ !this.state.editing }
            html={ this.state.html }
            onChange={ this.onProcessContentChange.bind(this) }
            onBlur={ this.onSanitizeContent.bind(this) }
          />
        { this.renderCommentLikeButton() }
        </div>
        { this.renderPersonSelectMenu() }
        { this.renderActions() }
      </div>
    )
  }

  renderCommentLikeButton() {
    if (!this.state.editing) {
      return (
        <div className='comment-like-meta'>
          <CommentLikeButton
            likedBy={ this.props.comment.get('likedBy') }
            commentId={ this.props.comment.get('slug') }
            user={ this.props.user }
            onLikeComment={ this.props.onLikeComment }
            onUnLikeComment={ this.props.onUnLikeComment }
          />
        </div>
      )
    } else {
      return null;
    }
  }

  renderPersonSelectMenu() {
    if (this.state.editing && this.state.peopleTextSearchString) {
      return (
        <CommentPeopleMenu
          peopleTextSearchString={ this.state.peopleTextSearchString }
          peopleSearchObject={ this.props.peopleSearchObject }
          onSearchForPeople={ this.props.onSearchForPeople }
          onSelectPerson={ this.onInjectPersonMeta.bind(this) }
        />
      )
    } else return null;
  }

  renderPersonMeta() {
    if (this.props.comment.get('userSlug')) {
      return (
        <div className='person-meta'>
          <div>
            <Link to={ `/people/${ this.props.comment.get('userSlug') }` }>{ this.props.comment.get('userName') }</Link>
          </div>
          <div>
            { `${ DateMaster.timeSince(this.props.comment.get('createdAt')) } ago` }
          </div>
        </div>
      )
    } else return null;
  }

  renderActions() {
    if (this.state.editing) {
      if (this.props.comment.get('slug')) {
        return (
          <div className='comment-actions'>
            <Link to='#' onClick={ event => this.onCancelEdit(event) }>cancel</Link>
            <span className='dot-spacer'></span>
            <Link to='#' onClick={ event => this.onSave(event) }>save</Link>
            <span className='dot-spacer'></span>
            <Link to='#' onClick={ event => this.onDelete(event) }>delete</Link>
          </div>
        )
      } else {
        return (
          <div className='comment-actions'>
            <Link to='#' onClick={ event => this.onCancelNewComment(event) }>cancel</Link>
            <span className='dot-spacer'></span>
            <Link to='#' onClick={ event => this.onSave(event) }>save</Link>
          </div>
        )
      }
    } else if (this.props.user.get('slug') === this.props.comment.get('userSlug')) {
      return (
        <div className='comment-actions'>
          <Link to='#' onClick={ event => this.onEdit(event) }>edit</Link>
        </div>
      )
    } else return null;
  }

  onProcessContentChange = (e) => {
    const html = e.target.value;
    if (html !== this.state.html) {
      this.setState({ html:  html });
      this.lookForPeopleText(html);
    }
  };

  onSanitizeContent = () => {
    const sanitizeConfiguration = {
      allowedTags: ['b', 'i', 'a', 'p', 'br', 'div'],
      allowedAttributes: { a: ['href','data-people-slug'] }
    };
    const sanitizedHtml = sanitizeHtml(this.state.html, sanitizeConfiguration);
    this.setState({ html:  sanitizedHtml });
  }

  lookForPeopleText(html) {
    const peopleTextMatches = html.match(AT_MATCH_REGEX);
    if (peopleTextMatches && peopleTextMatches.length > 0) {
      this.setState({ peopleTextSearchString: peopleTextMatches[0].replace('@','') });
    } else {
      this.setState({ peopleTextSearchString: null });
    }
  }

  onInjectPersonMeta(user) {
    const peopleTextMatches = this.state.html.match(AT_MATCH_REGEX);
    if (peopleTextMatches && peopleTextMatches.length > 0) {
      const displayName = user.get('name') || user.get('slug');
      const slug = user.get('slug');
      const userElement = `<a href='/people/${ slug }' data-people-slug='${ slug }'>@${ displayName }</a>`;
      const newHtml = this.state.html.repeat(1).replace(`${ peopleTextMatches[0] }`, userElement);
      this.setState({ html: newHtml });
      this.contentEditableRef.current.focus();
    }
  }

  onEdit(event) {
    event.preventDefault();
    this.setState({ editing: true });
  }

  onCancelEdit(event) {
    event.preventDefault();
    this.setState({ editing: false });
  }

  onCancelNewComment(event) {
    event.preventDefault();
    this.props.onCancelNewComment();
  }

  onSave(event) {
    event.preventDefault();
    this.setState({ editing: false });
    this.props.onSaveComment(this.props.comment.get('slug'), this.state.html, this.props.comment.get('wordText'));
  }

  onDelete(event) {
    event.preventDefault();
    const slug = this.props.comment.get('slug');
    if (slug) {
      this.props.onDeleteComment(this.props.comment);
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/comments/comment.jsx